import {ChangeDetectionStrategy, Component, computed, effect, input, model, signal, untracked} from "@angular/core"
import {MatTooltipModule} from "@angular/material/tooltip"
import {NumericFieldComponent} from "@app/common/components/inputs/numeric-field/numeric-field.component"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-linked-dimension-input",
    templateUrl: "./linked-dimension-input.component.html",
    styleUrls: ["./linked-dimension-input.component.scss"],
    imports: [NumericFieldComponent, MatTooltipModule],
})
export class LinkedDimensionInputComponent {
    readonly $width = model<number | undefined>(undefined, {alias: "width"})
    readonly $height = model<number | undefined>(undefined, {alias: "height"})
    readonly $preserveAspectRatio = model<boolean>(true, {alias: "preserveAspectRatio"})
    readonly $forcedAspectMode = input(false, {alias: "forcedAspectMode"})
    readonly $aspectRatio = input<number | undefined>(undefined, {alias: "aspectRatio"})
    readonly $decimalPlaces = input<number>(2, {alias: "decimalPlaces"})
    readonly $widthLabel = input<string>("Width", {alias: "widthLabel"})
    readonly $heightLabel = input<string>("Height", {alias: "heightLabel"})
    readonly $widthPlaceholder = input<string | undefined>(undefined, {alias: "widthPlaceholder"})
    readonly $heightPlaceholder = input<string | undefined>(undefined, {alias: "heightPlaceholder"})
    readonly $disabled = input(false, {alias: "disabled"})

    // ratio captured from the current width/height when the user manually engages the link
    // (only relevant when the caller doesn't impose an explicit aspectRatio)
    private readonly $derivedRatio = signal<number | undefined>(undefined)
    private readonly $effectiveRatio = computed(() => this.$aspectRatio() ?? this.$derivedRatio())

    constructor() {
        effect(() => {
            if (this.$forcedAspectMode()) {
                untracked(() => this.$preserveAspectRatio.set(true))
            }
        })

        // the target ratio itself becomes known or changes (e.g. an upload finishes processing) ->
        // width is the anchor; fall back to height only if width isn't set yet
        effect(() => {
            const ratio = this.$effectiveRatio()
            const preserve = this.$preserveAspectRatio()
            if (!preserve || ratio === undefined || !isFinite(ratio) || ratio <= 0) return
            untracked(() => {
                const width = this.$width()
                const height = this.$height()
                if (width !== undefined) {
                    const newHeight = width / ratio
                    if (newHeight !== height) this.$height.set(newHeight)
                } else if (height !== undefined) {
                    const newWidth = height * ratio
                    if (newWidth !== width) this.$width.set(newWidth)
                }
            })
        })
    }

    // Kept synchronous (not effect()-driven) so the paired field is already updated before a Tab
    // key can move focus to it — effects flush as a microtask, which is too late for that.
    protected onWidthChange(value: number | undefined): void {
        this.$width.set(value)
        if (!this.$preserveAspectRatio() || value === undefined) return
        const ratio = this.$effectiveRatio()
        if (ratio === undefined || !isFinite(ratio) || ratio <= 0) return
        this.$height.set(value / ratio)
    }

    protected onHeightChange(value: number | undefined): void {
        this.$height.set(value)
        if (!this.$preserveAspectRatio() || value === undefined) return
        const ratio = this.$effectiveRatio()
        if (ratio === undefined || !isFinite(ratio) || ratio <= 0) return
        this.$width.set(value * ratio)
    }

    protected toggleLink(): void {
        if (this.$forcedAspectMode()) return
        const preserve = !this.$preserveAspectRatio()
        if (preserve && this.$aspectRatio() === undefined) {
            const width = this.$width()
            const height = this.$height()
            if (width !== undefined && height !== undefined && height !== 0) {
                this.$derivedRatio.set(width / height)
            }
        }
        this.$preserveAspectRatio.set(preserve)
    }
}
